import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Typography,
} from "@mui/material"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { BoundTextField } from "lib/@components/binding/bound-components"
import { Bound } from "lib/@components/binding/Bound"
import { If } from "lib/@components/switch"
import { useMemo, useState } from "react"
import { putMaintenanceCategory } from "../../controller/maintenance-library"
import { useRefresh } from "lib/@hooks/useRefresh"

const TYPE_CONFIG = {
    directory: { title: "Add New Folder", nameLabel: "Folder Name", defaultName: "New Folder" },
    file: { title: "Import Source File", nameLabel: "File Name", defaultName: "New File" },
    taskGroup: { title: "Add Component Group", nameLabel: "Group Name", defaultName: "New Component Group" },
}

export function CategoryEditor({ open, onClose, parentId, type = "directory" }) {
    const { refresh: explorerRefresh } = useBoundContext()
    const [processing, setProcessing] = useState(false)
    const refresh = useRefresh()
    const config = TYPE_CONFIG[type] || TYPE_CONFIG.directory

    const target = useMemo(
        () => ({
            name: config.defaultName,
            type: type === "taskGroup" ? "file" : type,
            parentId: parentId,
            originalText: "",
            authored: type === "taskGroup",
        }),
        [type, parentId, config.defaultName]
    )

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
            <Bound target={target} refresh={refresh}>
                <DialogTitle>{config.title}</DialogTitle>
                <DialogContent>
                    <Stack spacing={3} sx={{ mt: 2 }}>
                        <BoundTextField field="name" label={config.nameLabel} autoFocus />

                        <If value={type === "taskGroup"}>
                            <Typography variant="body2" color="text.secondary">
                                A component group is a container for manually authored components. You can add
                                components directly without importing from a source file.
                            </Typography>
                        </If>

                        <If value={type === "file"}>
                            <BoundTextField
                                field="originalText"
                                label="Original Source Text"
                                multiline
                                rows={15}
                                sx={{ "& textarea": { fontFamily: "monospace", fontSize: "0.875rem" } }}
                            />
                        </If>

                        {processing && (
                            <Stack direction="row" spacing={2} alignItems="center" justifyContent="center">
                                <CircularProgress size={24} />
                                <Typography variant="body2">
                                    {type === "file" ? "Extracting and ingesting components..." : "Creating..."}
                                </Typography>
                            </Stack>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} disabled={processing}>
                        Cancel
                    </Button>
                    <Button variant="contained" color="primary" disabled={processing} onClick={handleSave}>
                        {type === "taskGroup" ? "Create" : "Save"}
                    </Button>
                </DialogActions>
            </Bound>
        </Dialog>
    )

    function handleClose() {
        if (!processing) {
            onClose()
        }
    }

    async function handleSave() {
        setProcessing(true)
        try {
            await putMaintenanceCategory(target)
            await explorerRefresh()
            onClose()
        } finally {
            setProcessing(false)
        }
    }
}
